var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import get from 'lodash/get';
import { getHub, MESSAGETYPES } from 'conversional-iframe-communication';
import { urlHelpers } from 'conversional-api';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import mergeWith from 'lodash/mergeWith';
// @ts-ignore
import * as domUtils from 'conversional-utils/dom';
import { getItem, setItem } from 'conversional-persistent-storage';
var INIT_PATH = 'conversionalLauncher.journeyConfig';
var SETTINGS_PATH = 'conversionalLauncher.journeySettings';
var CONVERSIONAL_REFERRER_STORAGE_KEY = 'hostReferrer';
export var getSettings = function () { return get(window, SETTINGS_PATH, null); };
export var getConfig = function () {
    var config = get(window, "".concat(INIT_PATH, ".configuration"), null);
    if (isEmpty(config))
        return null;
    config.updated = get(window, "".concat(INIT_PATH, ".updated"), null);
    return config;
};
export var getJourneyId = function () { return getConfig().id || '(not set)'; };
var replaceArrays = function (_objValue, srcValue) {
    // lodash merges arrays index-wise, which would splice a shorter integration
    // filter into a longer host one (['x'] over ['a','b'] -> ['x','b']).
    // Returning undefined for non-arrays keeps merge's default behaviour, which
    // skips `undefined` sources but does assign `null` - the explicit "clear
    // this filter" signal setExternalHost relies on.
    return Array.isArray(srcValue) ? srcValue : undefined;
};
/**
 * The host page owns two objects, both shaped `{ data, config }`:
 *
 * - `window.conversionalWidget` is the historical one, and on some hosts it is
 *   also written by the host's *own* application code (a page component, a tag
 *   manager tag) - which means it can be, and on porta.de is, reset wholesale
 *   after our integration snippet has filled it, silently dropping the
 *   bookables filter and iframe config the snippet had put there.
 * - `window.conversionalIntegration` is reserved for the integration snippet
 *   we ship for that host (see e.g. public/custom/porta/start.js). Nothing on
 *   the host page writes it, so what the snippet puts there survives.
 *
 * Everything that reads host-provided data must go through this function so
 * both objects are honoured: `conversionalIntegration` wins per key, since it
 * is the one whose writer we control.
 */
export var getHostWidget = function () {
    var widget = window.conversionalWidget;
    var integration = window.conversionalIntegration;
    if (!integration)
        return widget;
    if (!widget)
        return integration;
    return mergeWith({}, widget, integration, replaceArrays);
};
export var trackingEnabled = function () {
    return get(getHostWidget(), 'config.tracking.enabled', true);
};
export var disableCookies = function () {
    var settings = getSettings();
    var cookieKeys = ['noCookies', 'noCookie', 'nocookies', 'nocookie'];
    return cookieKeys.some(function (key) { return settings[key] === 'true'; });
};
/**
 * Snapshot of the host location. The widget renders conditionally on it, and on
 * SPA/PWA hosts the URL changes without a reload, so it has to be re-read on
 * every publish.
 */
export var getHostLocation = function () { return ({
    hostUrl: window.location.href,
    hostQueryParams: urlHelpers.parseQueryString(),
}); };
var buildExternalData = function () {
    // Read Data
    // DEPRECATED FUNCTION - reformat data to support Küche&Co with Questionnaire and CLosure keys
    var hostWidget = getHostWidget();
    var check = hostWidget === null || hostWidget === void 0 ? void 0 : hostWidget.configuration;
    var data;
    if (check === null || check === void 0 ? void 0 : check.Questionnaire) {
        var _a = check.Questionnaire.Closure, meta = _a.meta, customer = __rest(_a, ["meta"]);
        data = {
            data: {
                Customer: customer,
                meta: meta,
            },
        };
    }
    else {
        data = hostWidget;
    }
    data = __assign(__assign({}, (data || {})), getHostLocation());
    try {
        var hostReferrer = getItem(CONVERSIONAL_REFERRER_STORAGE_KEY);
        if (!hostReferrer && domUtils.isExternalReferrer()) {
            hostReferrer = document.referrer;
            setItem(CONVERSIONAL_REFERRER_STORAGE_KEY, hostReferrer);
        }
        data.hostReferrer = hostReferrer;
    }
    catch (e) {
        //@ts-ignore
    }
    return data;
};
/**
 * Fingerprint used to skip redundant publishes. It has to cover *both* host
 * objects (getHostWidget merges them): keying it on window.conversionalWidget
 * alone would swallow a publish whenever only window.conversionalIntegration
 * changed - which is every porta SPA navigation, since the snippet's own
 * writes land there.
 */
var hostSnapshot = function () {
    try {
        return JSON.stringify(getHostWidget());
    }
    catch (e) {
        // The host objects are written by the host page and aren't guaranteed to
        // be well-formed (e.g. a circular reference).
        return undefined;
    }
};
var publishExternalData = function () {
    var data = buildExternalData();
    if (data)
        getHub().publish(MESSAGETYPES.SET_EXTERNAL, data);
};
/**
 * Some host integrations (e.g. a CMS-rendered page component, as opposed to a
 * static snippet) write the host data objects asynchronously, and that write
 * can land after this iframe's own first read of them. Nothing re-reads them
 * afterwards unless the host later navigates (see captureHostUrlChange/
 * publishHostUpdate in spa.ts), so a bookables filter set
 * just slightly too late is silently dropped for the rest of the page view —
 * the widget falls back to its unfiltered default content. Re-check a few
 * times over a short window so a late host write still gets picked up.
 */
var EXTERNAL_DATA_RETRY_DELAYS_MS = [250, 750, 2000];
var externalDataRetriesScheduled = false;
var scheduleExternalDataRetries = function () {
    if (externalDataRetriesScheduled)
        return;
    externalDataRetriesScheduled = true;
    EXTERNAL_DATA_RETRY_DELAYS_MS.forEach(function (delay) {
        setTimeout(function () {
            var snapshot = hostSnapshot();
            if (snapshot === lastHostInfo)
                return;
            lastHostInfo = snapshot;
            publishExternalData();
        }, delay);
    });
};
var lastHostInfo;
/**
 * The "skip if unchanged" dedup below exists to avoid redundant publishes
 * when this is called repeatedly with the same host data. But SET_EXTERNAL
 * is delivered by broadcast (getHub().publish) to whichever iframes are
 * subscribed *at that moment* - a publish is not replayed to a later
 * subscriber. An iframe that finishes loading and subscribes only after an
 * earlier call already published this exact data would be skipped forever
 * by every later call, since the host data never actually changes value
 * again. `force` bypasses the dedup for the specific caller that is only safe
 * to call once every iframe is guaranteed to already be subscribed
 * (onIframesReady in createIframes.ts).
 */
export var applyExternalData = function (force) {
    if (force === void 0) { force = false; }
    var snapshot = hostSnapshot();
    if (force || snapshot !== lastHostInfo) {
        lastHostInfo = snapshot;
        publishExternalData();
    }
    scheduleExternalDataRetries();
};
/**
 * Sent when the host page navigates. Carries the refreshed location plus
 * whatever the host script wrote to `window.conversionalIntegration` /
 * `window.conversionalWidget` for the new page (branch and service filters are
 * the point of this), but never `process`:
 * that one re-injects the host's original service_type_id/branch_id and would
 * silently revert a selection the user already made in an open widget.
 */
export var publishHostUpdate = function () {
    var data = buildExternalData();
    if (data)
        getHub().publish(MESSAGETYPES.SET_EXTERNAL, omit(data, 'process'));
};
export var getScriptConfig = function () {
    var url = new URL(document.currentScript.getAttribute('src'));
    var scriptParams = Object.fromEntries(url.searchParams);
    return scriptParams;
};
